"use client";

import * as React from "react";
import { useEffect } from "react";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListSubheader from "@mui/material/ListSubheader";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { FiTag } from "react-icons/fi";
import AppModal from "./AppModal";
import labelService from "../services/labelService";

const btnCancel = {
  padding: "8px 20px",
  fontSize: "13px",
  fontWeight: "600",
  color: "#374151",
  backgroundColor: "#f3f4f6",
  border: "1.5px solid #e5e7eb",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.15s ease, border-color 0.15s ease",
};

const btnSave = {
  padding: "8px 22px",
  fontSize: "13px",
  fontWeight: "600",
  color: "#fff",
  backgroundColor: "#1a7d3e",
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.15s ease, filter 0.15s ease",
  boxShadow: "0 2px 6px rgba(26, 125, 62, 0.3)",
};

const btnSaveDisabled = {
  ...btnSave,
  opacity: 0.5,
  cursor: "not-allowed",
  boxShadow: "none",
};

const labelFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#e5e7eb", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#c6e8ce" },
    "&.Mui-focused fieldset": {
      borderColor: "#1a7d3e",
      boxShadow: "0 0 0 3px rgba(26, 125, 62, 0.12)",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#1a7d3e" },
};

const parentSelectSx = {
  borderRadius: "8px",
  backgroundColor: "#fff",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb", borderWidth: "1.5px" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#c6e8ce" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1a7d3e",
    boxShadow: "0 0 0 3px rgba(26, 125, 62, 0.12)",
  },
};

const checkboxSx = {
  color: "#d1d5db",
  "&.Mui-checked": { color: "#1a7d3e" },
};

function flattenLabels(labels, parentPath = "") {
  const seen = new Map();

  function walk(items, path = "") {
    for (const label of items) {
      const currentPath = path ? `${path} / ${label.name}` : label.name;

      if (!seen.has(label.id)) {
        seen.set(label.id, {
          id: label.id,
          name: currentPath,
        });
      }

      if (label.children && label.children.length > 0) {
        walk(label.children, currentPath);
      }
    }
  }

  walk(labels, parentPath);
  return Array.from(seen.values());
}

export function CreateLabelDialog({ open, setOpen, fetchAllData, labelId }) {
  const [name, setName] = React.useState("");
  const [nest, setNest] = React.useState(true);
  const [parent, setParent] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [labels, setLabels] = React.useState([]);
  const [searching, setSearching] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [selectedParent, setSelectedParent] = React.useState(null);
  const [searchInput, setSearchInput] = React.useState("");

  const typingTimeout = React.useRef(null);
  const abortControllerRef = React.useRef(null);

  useEffect(() => {
    if (open) {
      fetchLabels("");
      if (labelId) {
        setIsEditMode(true);
        fetchLabelDetails(labelId);
      } else {
        setIsEditMode(false);
        setName("");
        setParent(null);
        setNest(true);
        setSelectedParent(null);
      }
    } else {
      setSearchInput("");
      setLabels([]);
    }
  }, [open, labelId]);

  async function fetchLabels(search = "") {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setSearching(true);
      const res = await labelService.GET_Label_ALL_Search(search, controller.signal);

      if (controller.signal.aborted) return;

      const fetchedLabels = res.data?.labels || [];
      const flatLabels = flattenLabels(fetchedLabels);
      setLabels(flatLabels);
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED" || error.name === "AbortError") {
        return;
      }
      console.error("Error fetching labels:", error);
    } finally {
      if (abortControllerRef.current === controller) {
        setSearching(false);
      }
    }
  }

  async function fetchLabelDetails(id) {
    try {
      const res = await labelService.GET_Label_By_Id(id);
      const labelData = res.data?.data;

      if (labelData) {
        setName(labelData.name || "");

        if (labelData.ParentLabel && labelData.ParentLabel.id) {
          setParent(labelData.ParentLabel.id);
          setSelectedParent({
            id: labelData.ParentLabel.id,
            name: labelData.ParentLabel.name,
          });
          setNest(true);
        } else {
          setParent(null);
          setSelectedParent(null);
          setNest(false);
        }
      }
    } catch (error) {
      console.error("Error fetching label details:", error);
    }
  }

  async function handleSave() {
    if (!name.trim()) return;
    setLoading(true);

    const payload = {
      name,
      color: "#FF0000",
    };

    if (selectedParent && selectedParent.id) {
      payload.parentId = selectedParent.id;
    }

    if (isEditMode && (!selectedParent || !selectedParent.id)) {
      payload.parentId = null;
    }

    try {
      if (isEditMode && labelId) {
        await labelService.Update_Label_By_Id(labelId, payload);
      } else {
        await labelService.CREATE_Label(payload);
      }
      if (typeof fetchAllData === 'function') {
        await fetchAllData();
      }
      setOpen(false);
    } catch (error) {
      console.error("Error saving label:", error);
    } finally {
      setLoading(false);
    }
  }

  const saveLabel = loading
    ? isEditMode
      ? "Updating..."
      : "Creating..."
    : isEditMode
      ? "Update"
      : "Create";

  const canSave = Boolean(name.trim()) && !loading;

  return (
    <AppModal
      show={open}
      onClose={() => setOpen(false)}
      icon={<FiTag size={17} color="#fff" />}
      title={isEditMode ? "Edit Label" : "New Label"}
      subtitle={
        isEditMode
          ? "Update your label name or nesting"
          : "Create a label to organize your mail"
      }
      maxWidth="480px"
      footer={
        <>
          <button
            type="button"
            style={btnCancel}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e9eaec";
              e.currentTarget.style.borderColor = "#d1d5db";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#f3f4f6";
              e.currentTarget.style.borderColor = "#e5e7eb";
            }}
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            style={canSave ? btnSave : btnSaveDisabled}
            disabled={!canSave}
            onMouseEnter={(e) => {
              if (!canSave) return;
              e.currentTarget.style.backgroundColor = "#1a6b2e";
            }}
            onMouseLeave={(e) => {
              if (!canSave) return;
              e.currentTarget.style.backgroundColor = "#1a7d3e";
            }}
            onClick={handleSave}
          >
            {saveLabel}
          </button>
        </>
      }
    >
      <div className="label-dialog-form">
        <TextField
          label="Label name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
          sx={labelFieldSx}
        />

        <FormControlLabel
          control={
            <Checkbox checked={nest} onChange={(e) => setNest(e.target.checked)} sx={checkboxSx} />
          }
          label="Nest label under:"
          sx={{
            margin: 0,
            "& .MuiFormControlLabel-label": {
              fontSize: "13.5px",
              color: "#374151",
              fontWeight: 500,
            },
          }}
        />

        <FormControl fullWidth size="small" disabled={!nest} sx={{ margin: 0 }}>
          <InputLabel id="parent-label-select-label" sx={{ "&.Mui-focused": { color: "#1a7d3e" } }}>
            Parent label
          </InputLabel>
          <Select
            labelId="parent-label-select-label"
            label="Parent label"
            value={selectedParent?.id || ""}
            sx={parentSelectSx}
            onChange={(e) => {
              const selectedId = e.target.value;
              if (selectedId === "") {
                setParent(null);
                setSelectedParent(null);
                return;
              }
              const lbl = labels.find((l) => l.id === selectedId);
              setParent(selectedId);
              setSelectedParent(lbl || { id: selectedId, name: "Selected Label" });
            }}
            renderValue={(selectedId) => {
              if (!selectedId) return <em style={{ color: "#9ca3af" }}>None</em>;
              const lbl = labels.find((l) => l.id === selectedId) || selectedParent;
              return lbl?.name || selectedId;
            }}
            MenuProps={{
              autoFocus: false,
              PaperProps: {
                className: "label-dialog-select-menu",
                style: {
                  maxHeight: 400,
                  borderRadius: 8,
                  border: "1px solid #e9ecef",
                  boxShadow: "0 4px 16px rgba(26, 125, 62, 0.1)",
                },
              },
            }}
          >
            <ListSubheader disableSticky sx={{ background: "#fff", lineHeight: "unset", padding: "8px 10px" }}>
              <TextField
                size="small"
                autoFocus
                placeholder="Type to search..."
                fullWidth
                sx={labelFieldSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                  ),
                }}
                value={searchInput}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchInput(val);
                  if (typingTimeout.current) clearTimeout(typingTimeout.current);
                  typingTimeout.current = setTimeout(() => fetchLabels(val), 400);
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") {
                    e.stopPropagation();
                  }
                }}
              />
            </ListSubheader>
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {searching && labels.length === 0 ? (
              <MenuItem disabled>Searching...</MenuItem>
            ) : (
              labels
                .filter((lbl) => lbl.name.toLowerCase().includes(searchInput.toLowerCase()))
                .map((lbl) => (
                  <MenuItem key={lbl.id} value={lbl.id}>
                    {lbl.name}
                  </MenuItem>
                ))
            )}
            {!searching &&
              labels.filter((lbl) =>
                lbl.name.toLowerCase().includes(searchInput.toLowerCase())
              ).length === 0 && <MenuItem disabled>No labels found</MenuItem>}
          </Select>
        </FormControl>
      </div>
    </AppModal>
  );
}
